import { useEffect, useState } from "react";
import { api } from "@/lib/pfc";
import { Briefcase, MapPin, DollarSign, Check, ExternalLink, Play } from "lucide-react";

const TYPE_LABELS = {
  driver: "Driver / Rideshare",
  gig: "Gig",
  "part-time": "Part-Time",
  "full-time": "Full-Time",
  contract: "Contract",
};

function FeaturedJob({ job }) {
  const [playing, setPlaying] = useState(false);
  return (
    <section className="mb-16" data-testid={`featured-job-${job.job_id}`}>
      <div className="label text-pfc-gold mb-3">Featured Opportunity</div>
      <div className="pfc-card overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-0">
          {/* Video / Image side */}
          <div className="relative bg-gradient-to-br from-blue-700 via-indigo-700 to-pink-500 min-h-[320px] flex items-center justify-center">
            {job.video_url && playing ? (
              <video
                src={job.video_url}
                className="w-full h-full object-cover"
                controls
                autoPlay
                playsInline
                data-testid="featured-job-video"
              />
            ) : (
              <>
                <img
                  src={job.cover_image}
                  alt={job.company}
                  className="w-full h-full object-cover"
                />
                {job.video_url && (
                  <button
                    onClick={() => setPlaying(true)}
                    aria-label="Play promo video"
                    data-testid="featured-job-play-btn"
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition group"
                  >
                    <div className="w-20 h-20 rounded-full bg-pfc-gold flex items-center justify-center group-hover:scale-110 transition">
                      <Play className="w-8 h-8 text-ink-900 ml-1" fill="currentColor" />
                    </div>
                  </button>
                )}
              </>
            )}
          </div>

          {/* Details side */}
          <div className="p-8 lg:p-12 flex flex-col">
            <span className="label bg-pfc-gold text-ink-900 px-2 py-1 self-start mb-4">
              {TYPE_LABELS[job.employment_type] || job.employment_type}
            </span>
            <h2 className="font-display text-3xl lg:text-4xl text-white uppercase leading-tight">
              {job.title}
            </h2>
            <div className="font-sub uppercase tracking-[0.2em] text-pfc-gold text-xs mt-2">
              {job.company}
            </div>

            <p className="text-white/70 font-body leading-relaxed mt-5 whitespace-pre-line">
              {job.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              <div className="flex items-center gap-2 text-white/80 text-sm font-body">
                <DollarSign size={16} className="text-pfc-gold" /> {job.payout}
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm font-body">
                <MapPin size={16} className="text-pfc-gold" /> {job.location}
              </div>
            </div>

            {job.perks?.length > 0 && (
              <ul className="mt-6 space-y-2">
                {job.perks.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-sm font-body">
                    <Check size={16} className="text-pfc-gold mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}

            {job.promo_code && (
              <div className="mt-6 border border-pfc-gold/40 bg-pfc-gold/5 px-4 py-3 rounded-sm">
                <div className="label text-pfc-gold text-[10px] mb-1">Promo Code</div>
                <div className="font-display text-2xl text-white tracking-wider">{job.promo_code}</div>
                <div className="text-white/60 text-xs mt-1 font-body">Use this code at signup to unlock the New Driver Guarantee.</div>
              </div>
            )}

            <a
              href={job.apply_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`apply-now-${job.job_id}`}
              className="mt-8 inline-flex items-center justify-center gap-2 bg-pfc-gold text-ink-900 px-8 py-4 font-sub uppercase tracking-[0.2em] text-sm hover:bg-white transition self-start"
            >
              Work Now → <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function JobCard({ job }) {
  return (
    <div className="pfc-card p-6 flex flex-col" data-testid={`job-card-${job.job_id}`}>
      <div className="flex items-center gap-3 mb-4">
        {job.cover_image ? (
          <img src={job.cover_image} alt={job.company} className="w-12 h-12 object-cover rounded-sm" />
        ) : (
          <div className="w-12 h-12 bg-pfc-gold/10 flex items-center justify-center rounded-sm">
            <Briefcase className="w-6 h-6 text-pfc-gold" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="label text-pfc-gold text-[10px]">{TYPE_LABELS[job.employment_type] || job.employment_type}</div>
          <div className="font-sub uppercase tracking-[0.15em] text-xs text-white truncate">{job.company}</div>
        </div>
      </div>
      <h3 className="font-display text-xl text-white uppercase leading-tight">{job.title}</h3>
      <p className="text-white/60 text-sm font-body mt-3 line-clamp-3">{job.description}</p>
      <div className="mt-4 space-y-1.5 text-white/70 text-sm font-body">
        <p className="flex items-center gap-2"><DollarSign size={14} className="text-pfc-gold" />{job.payout}</p>
        <p className="flex items-center gap-2"><MapPin size={14} className="text-pfc-gold" />{job.location}</p>
      </div>
      <a
        href={job.apply_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`apply-${job.job_id}`}
        className="mt-5 inline-flex items-center justify-center gap-2 border border-pfc-gold text-pfc-gold px-5 py-3 font-sub uppercase tracking-[0.2em] text-xs hover:bg-pfc-gold hover:text-ink-900 transition"
      >
        Work Now → <ExternalLink size={12} />
      </a>
    </div>
  );
}

export default function Jobs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const featured = items.find((j) => j.featured);
  const rest = items.filter((j) => !featured || j.job_id !== featured.job_id);

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14" data-testid="jobs-page">
      <div className="mb-12">
        <div className="label text-pfc-gold mb-3">Jobs · Gigs · Work</div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
          Find Your Next <span className="gold-text">Hustle</span>
        </h1>
        <p className="text-white/60 font-body mt-4 max-w-2xl">
          Real opportunities posted by PFC and our partners. Drive, deliver, design, perform — pick your lane and work on your terms.
        </p>
      </div>

      {loading && (
        <div className="text-white/50 font-body" data-testid="jobs-loading">Loading opportunities…</div>
      )}

      {!loading && featured && <FeaturedJob job={featured} />}

      {!loading && rest.length > 0 && (
        <>
          <div className="label text-pfc-gold mb-4">More Opportunities</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jobs-grid">
            {rest.map((j) => <JobCard key={j.job_id} job={j} />)}
          </div>
        </>
      )}

      {!loading && items.length === 0 && (
        <div className="pfc-card p-10 text-center" data-testid="jobs-empty">
          <Briefcase className="w-12 h-12 text-pfc-gold mx-auto mb-4" />
          <h3 className="font-display text-2xl text-white uppercase">No openings posted yet</h3>
          <p className="text-white/60 font-body mt-2">Check back soon — fresh gigs land here weekly.</p>
        </div>
      )}

      {/* Post a job CTA */}
      <div className="mt-16 pfc-card p-8 lg:p-12 text-center" data-testid="post-job-cta">
        <div className="label text-pfc-gold mb-3">Hiring?</div>
        <h3 className="font-display text-2xl lg:text-3xl text-white uppercase">Post a Job on PFC</h3>
        <p className="text-white/60 font-body mt-3 max-w-xl mx-auto">
          Reach our community of creators, drivers, artists, and freelancers. Email us the role + apply link and we&apos;ll get it live.
        </p>
        <a
          href="/contact?subject=Job+Listing+Submission"
          data-testid="post-job-btn"
          className="mt-6 inline-flex items-center gap-2 bg-pfc-gold text-ink-900 px-8 py-4 font-sub uppercase tracking-[0.2em] text-sm hover:bg-white transition"
        >
          Submit a Listing →
        </a>
      </div>
    </div>
  );
}
